import { PrivacyPage, TermsPage, NotFoundPage } from "./pages/Legal";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store";
import { AppShell }    from "./components/AppShell";
import { Toaster }     from "./components/Toaster";
import { ResumeGate }  from "./components/ResumeGate";
import { LoadingState } from "./components/LoadingState";
import { Dashboard }   from "./pages/Dashboard";
import { Matches }     from "./pages/Matches";
import { ApplyQueue }  from "./pages/ApplyQueue";
import { Profile }     from "./pages/Profile";
import { Billing }     from "./pages/Billing";
import { Admin }       from "./pages/Admin";
import { Extensions }  from "./pages/Extensions";
import { Login }       from "./pages/Login";
import { Landing }     from "./pages/Landing";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword }  from "./pages/ResetPassword";
import { VerifyEmail }    from "./pages/VerifyEmail";
import { OAuthCallback } from "./pages/OAuthCallback";
import { PartnerUsers } from "./pages/PartnerUsers";
import { ResumeOnboarding } from "./pages/ResumeOnboarding";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore(s => s.token);
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

export function App() {
  const token       = useAuthStore(s => s.token);
  const hasHydrated = useAuthStore(s => s.hasHydrated);

  // Hold every route decision below until the persisted auth store has been
  // read — otherwise a hard reload can render Login/Landing for one frame
  // before flipping to the authenticated app (see store/index.ts).
  if (!hasHydrated) {
    return (
      <BrowserRouter>
        <LoadingState fullPage />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      {/* App-wide toast stack — mounted once so any action (Save, etc.) can
          acknowledge, on both the authed app and guest routes. */}
      <Toaster />
      <Routes>
        <Route path="/login"           element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/oauth-callback"  element={<OAuthCallback />} />
        {/* Resume onboarding — a focused full-page gate, deliberately outside
            AppShell (no nav chrome to browse away into). Reachable only when
            authenticated; ResumeGate below redirects resume-dependent pages
            here, and it self-redirects back out once a resume is active. */}
        <Route path="/resume-onboarding" element={<RequireAuth><ResumeOnboarding /></RequireAuth>} />
        {/* Root: the marketing landing for guests, the app for members — same URL. */}
        {token ? (
          <Route path="/" element={<AppShell />}>
            {/* Resume-dependent pages only — Profile/Billing/Admin/Extensions stay
                reachable with no resume (account settings, privacy, logout, and
                admin workflows must never be blocked by this gate). */}
            <Route index element={<ResumeGate><Dashboard /></ResumeGate>} />
            <Route path="matches"    element={<ResumeGate><Matches /></ResumeGate>} />
            <Route path="queue"      element={<ResumeGate><ApplyQueue /></ResumeGate>} />
            <Route path="profile"    element={<Profile />} />
            <Route path="billing"    element={<Billing />} />
            <Route path="admin"      element={<Admin />} />
            <Route path="extensions" element={<Extensions />} />
          </Route>
        ) : (
          <Route path="/" element={<Landing />} />
        )}
        {/* ExH1B Partner Review — standalone, read-only, server-scoped to ExH1B users. */}
        <Route path="/partner" element={<RequireAuth><PartnerUsers /></RequireAuth>} />
        {/* v1.1: /privacy + /terms (LEGAL_PAGES_ENABLED) and the 404 page (RELIABILITY_V11_ENABLED);
            each redirects to "/" exactly as before while its flag is off. */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms"   element={<TermsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
